import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/useToast';
import { Mail, Lock, Eye, EyeOff, Loader2, User, Building2, Calendar } from 'lucide-react';
import LoginLayout, { useLoginStyles } from '@/components/layout/LoginLayout';


export default function Register() {
  const navigate = useNavigate();
  const { register: registerUser, googleLogin } = useAuth();
  const { success, error: showError } = useToast();
  const styles = useLoginStyles();
  const { t } = styles;

  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', organizationName: '' });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 6) e.password = 'Must be at least 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    if (!form.organizationName.trim()) e.organizationName = 'Organization name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await registerUser(form.name, form.email, form.password, form.organizationName);
      success('Account created!', "Welcome to ScheduleX. Let's set up your first calendar.");
      navigate('/org/overview');
    } catch (err) {
      showError('Registration failed', err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!window.google?.accounts?.id) {
      showError('Google Sign-In not loaded', 'Please refresh the page and try again.');
      return;
    }
    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      callback: (response) => {
        if (response.credential) {
          setLoading(true);
          googleLogin(response.credential)
            .then(() => {
              success('Welcome!', 'You have been signed in with Google.');
              navigate('/org/overview');
            })
            .catch((err) => {
              showError('Google login failed', err.response?.data?.message || 'Could not authenticate with Google');
            })
            .finally(() => setLoading(false));
        }
      },
      auto_select: false,
    });
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        showError('Google popup blocked', 'Please allow popups for this site and try again.');
      }
    });
  };

  const field = (label, key, type = 'text', icon, placeholder, extra = {}) => (
    <div className="mb-4">
      <label className="form-label" style={styles.label}>{label}</label>
      <div className="relative">
        {icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: t.text4 }}>{icon}</div>}
        <input
          type={type} value={form[key]} onChange={(e) => update(key, e.target.value)}
          className="form-input" style={{ ...styles.input, paddingLeft: icon ? '40px' : '16px', paddingRight: (key === 'password' || key === 'confirmPassword') ? '44px' : '16px' }}
          placeholder={placeholder} autoComplete={extra.autoComplete || 'off'}
          onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
          onBlur={(e) => { e.target.style.borderColor = errors[key] ? t.error : t.inputBorder; e.target.style.background = t.inputBg; e.target.style.boxShadow = 'none'; }}
        />
        {(key === 'password' || key === 'confirmPassword') && (
          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors" style={{ color: t.text4, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => key === 'password' ? setShowPass(!showPass) : setShowConfirm(!showConfirm)} onMouseEnter={(e) => e.currentTarget.style.color = t.text2} onMouseLeave={(e) => e.currentTarget.style.color = t.text4}>
            {(key === 'password' ? showPass : showConfirm) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {errors[key] && <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: t.error }}><span>!</span><span>{errors[key]}</span></div>}
    </div>
  );

  const content = (
    <>
      {/* Header */}
      <div className="mb-7 text-center">
        <h2 className="text-2xl font-extrabold tracking-tight mb-1.5" style={{
          color: t.text,
          background: t.gradientText,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Create your account
        </h2>
        <p className="text-sm" style={{ color: t.text3 }}>Start scheduling smarter, for free.</p>
      </div>

      {/* Sign in CTA */}
      <div className="mb-5 p-4 rounded-2xl" style={{ background: t.inputBg, border: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.border2 }}>
            <Calendar className="w-5 h-5" style={{ color: t.text2 }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold" style={{ color: t.text }}>Already a member?</div>
            <div className="text-xs" style={{ color: t.text3 }}>Sign in to manage your bookings.</div>
          </div>
          <Link to="/login" className="text-xs font-bold px-3 py-2 rounded-lg transition whitespace-nowrap" style={styles.btnPrimary}>
            Sign in
          </Link>
        </div>
      </div>

      {/* Social login */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition" style={styles.btnPrimary} onClick={handleGoogleLogin}>
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Google
        </button>
        <button className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition" style={styles.btnPrimary} onClick={() => showError('Coming soon', 'Apple Sign-In is not yet enabled.')}> 
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
          Apple
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 mb-5">
        <div style={styles.divider} />
        <span style={styles.dividerText}>OR USE EMAIL</span>
        <div style={styles.divider} />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        {field('Full name', 'name', 'text', <User className="w-4 h-4" />, 'John Doe', { autoComplete: 'name' })}
        {field('Email address', 'email', 'email', <Mail className="w-4 h-4" />, 'you@company.com', { autoComplete: 'email' })}
        {field('Organization name', 'organizationName', 'text', <Building2 className="w-4 h-4" />, 'Acme Inc.', { autoComplete: 'organization' })}
        {field('Password', 'password', showPass ? 'text' : 'password', <Lock className="w-4 h-4" />, 'Create a password', { autoComplete: 'new-password' })}
        {field('Confirm password', 'confirmPassword', showConfirm ? 'text' : 'password', <Lock className="w-4 h-4" />, 'Repeat your password', { autoComplete: 'new-password' })}

        <button type="submit" disabled={loading} className="w-full font-bold py-3.5 rounded-xl text-sm flex items-center justify-center transition" style={styles.btnSolid}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating account…</> : 'Create account'}
        </button>
      </form>

      <p className="text-center text-sm mt-6" style={{ color: t.text3 }}>
        By signing up, you agree to our <Link to="/terms" className="font-bold hover:underline" style={{ color: t.text }}>Terms</Link> and <Link to="/privacy" className="font-bold hover:underline" style={{ color: t.text }}>Privacy Policy</Link>.
      </p>
    </>
  );

  return <LoginLayout showTabs={false}>{content}</LoginLayout>;
}
